import { useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/card/Card';
import Icon from '../components/icon/Icon';
import NavBar from './NavBar';
import Tooltip from '../components/tooltip/Tooltip';
import { routes } from '../routes/routePaths';
import useRole from '../hooks/useRole';
import { useClientTheme } from '../context/ThemeContext';
import './Layout.scss';

interface LayoutProps {
  children: React.ReactNode;
}

interface RoleIcon {
  width: number;
  height: number;
  name: string;
  route: string;
  msg: string;
  matchRoutes?: (string | RegExp)[];
}

const config = JSON.parse(localStorage.getItem('config') ?? '{}');
const isClientEnabled = config?.isClientEnabled ?? false;
const isGuaranteesEnabled = config?.isGuaranteesEnabled ?? false;
const isQuotationsEnabled = config?.isQuotationsEnabled ?? false;
const isInstrumentationEnabled = config?.isInstrumentationEnabled ?? true;

const COMMON_ICONS: RoleIcon[] = [
  {
    name: 'home_outline',
    width: 24,
    height: 24,
    route: routes.dashboardComercial,
    msg: 'Dashboard',
    matchRoutes: [routes.dashboardComercial, '/dashboard'],
  },
  {
    name: 'origination_outline',
    width: 24,
    height: 24,
    route: routes.dashboard,
    msg: 'Originacion',
    matchRoutes: [routes.dashboard, routes.clients],
  },
  {
    name: 'risk_outline',
    width: 24,
    height: 24,
    route: routes.risksAnalysis,
    msg: 'Análisis de riesgos',
    matchRoutes: [routes.risksAnalysis],
  },
  {
    name: 'committee_outline',
    width: 24,
    height: 24,
    route: routes.committee,
    msg: 'Comité',
    matchRoutes: [routes.committee],
  },
  {
    name: 'instrumentation_outline',
    width: 40,
    height: 40,
    route: routes.instrumentation,
    msg: 'Instrumentación',
    matchRoutes: [routes.instrumentation],
  },
  {
    name: 'folder_outline',
    width: 24,
    height: 24,
    route: routes.finishedFolders,
    msg: 'Carpetas finalizadas',
    matchRoutes: [routes.finishedFolders],
  },
];

const USERS_ICON: RoleIcon = {
  name: 'users_outline',
  width: 24,
  height: 24,
  route: routes.users,
  msg: 'Usuarios',
  matchRoutes: [routes.users],
};

const CLIENT_ICON: RoleIcon[] = [
  {
    name: 'users_3_outline',
    width: 24,
    height: 24,
    route: routes.clientsList,
    msg: 'Clientes',
    matchRoutes: [
      routes.clientsList,
      routes.clientDetail,
      new RegExp(`^${routes.clients}/[^/]+/application/[^/]+$`),
    ],
  },
];

const GUARANTEES_ICON: RoleIcon[] = [
    {
    name: 'protector_icon',
    width: 20,
    height: 20,
    route: routes.fundsAndProtectors,
    msg: 'Fondos y protectores',
    matchRoutes: [routes.fundsAndProtectors],
  },
  {
    name: 'monetization',
    width: 24,
    height: 24,
    route: routes.monetization,
    msg: 'Monetización',
    matchRoutes: [routes.monetization, routes.newMonetization, routes.editMonetization],
  },
  {
    name: 'search_eye',
    width: 24,
    height: 24,
    route: routes.guarantees,
    msg: 'Gestión de avales',
    matchRoutes: [routes.guarantees, routes.guaranteeDetail, routes.productLineDetail, routes.checkDetail, routes.loanDetail, routes.promissoryNoteDetail],
  },
  {
    name: 'overdue',
    width: 24,
    height: 24,
    route: routes.overdue,
    msg: 'Vencimientos',
    matchRoutes: [routes.overdue],
  },
  {
    name: 'annexes',
    width: 24,
    height: 24,
    route: routes.annexes,
    msg: 'Anexos',
    matchRoutes: [routes.annexes],
  },
];

const SETTINGS_ICON: RoleIcon = {
  name: 'settings_outline',
  width: 24,
  height: 24,
  route: routes.settings,
  msg: 'Configuración',
  matchRoutes: [routes.settings, routes.rateTypes, routes.sgr],
};


const roleIcons = {
  producer: [
    {
      name: 'origination_outline',
      width: 24,
      height: 24,
      route: routes.dashboard,
      msg: 'Inicio',
      matchRoutes: [routes.dashboard],
    },
  ],
  commercial: COMMON_ICONS,
  comite: COMMON_ICONS,
  instrumentator: COMMON_ICONS,
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isProducer, isCommercial, isComite, isInstrumentator } = useRole();
  const { clientTheme } = useClientTheme();

  const userIcons: RoleIcon[] = [];
  if (isProducer) userIcons.push(...roleIcons.producer);
  if (isCommercial) userIcons.push(...roleIcons.commercial);
  if (isComite) userIcons.push(...roleIcons.comite);
  if (isInstrumentator) userIcons.push(...roleIcons.instrumentator);

  return (
    <div 
      style={{ 
        display: 'grid', 
        minHeight: '100vh', 
        width: '100%', 
        maxWidth: '100vw', 
        overflowX: 'hidden'
      }}
    >
      <Card width='-webkit-fill-available' maxWidth='100vw'>
        <NavBar />
        <Card display='flex' height='calc(100% - 58px)'>
          <Card
            variant='neutral'
            width='72px'
            borderRadius={false}
            modifier='inverted'
            justifyItems='center'
            spacing={{ top: '4' }}
          >
            <Card display='grid' gap='10px'>
              {userIcons.map((icon) => {
                // Skip instrumentation icon if not enabled
                if (icon.name === 'instrumentation_outline' && !isInstrumentationEnabled) {
                  return null;
                }

                const isActive =
                  icon.matchRoutes?.some((route) => {
                    if (route instanceof RegExp) {
                      return route.test(location.pathname);
                    }
                    if (route === routes.clients) {
                      return location.pathname === route;
                    }
                    if (route === routes.clientDetail) {
                      return location.pathname.startsWith('/clientes/') && 
                             !location.pathname.startsWith('/clientes/list');
                    }
                    return location.pathname.startsWith(route);
                  }) ?? false;

                return (
                  <Card key={icon.name}>
                    <Card
                      display='flex'
                      variant={isActive ? (clientTheme ? 'primary' : 'success') : 'none'}
                      modifier='weak'
                      height='36px'
                      justifyContent='center'
                      width='36px'
                      alignItems='center'
                    >
                      <Card>
                        <Tooltip position='right' msg={icon.msg}>
                          <Icon
                            name={icon.name}
                            variant={isActive ? 'primary' : 'neutral'}
                            modifier='strong'
                            width={icon.width}
                            height={icon.height}
                            cursor='pointer'
                            onClick={() => navigate(icon.route)}
                          />
                        </Tooltip>
                      </Card>
                    </Card>
                  </Card>
                );
              })}
              
              {isGuaranteesEnabled && (isCommercial || isComite || isInstrumentator) &&  (
                <>
                  <div style={{ 
                    borderTop: '1px solid #E0E0E0',
                    width: '100%'
                  }} />
                  {GUARANTEES_ICON.map((icon) => {
                    const isActive =
                      icon.matchRoutes?.some((route) => {
                        if (route instanceof RegExp) {
                          return route.test(location.pathname);
                        }
                        if (route === routes.clients) {
                          return location.pathname === route;
                        }
                        if (route === routes.clientDetail) {
                          return location.pathname.startsWith('/clientes/') && 
                                 !location.pathname.startsWith('/clientes/list');
                        }
                        return location.pathname.startsWith(route);
                      }) ?? false;

                    return (
                      <Card key={icon.name}>
                        <Card
                          display='flex'
                          variant={isActive ? (clientTheme ? 'primary' : 'success') : 'none'}
                          modifier='weak'
                          height='36px'
                          justifyContent='center'
                          width='36px'
                          alignItems='center'
                        >
                          <Card>
                            <Tooltip position='right' msg={icon.msg}>
                              <Icon
                                name={icon.name}
                                variant={isActive ? 'primary' : 'neutral'}
                                modifier='strong'
                                width={icon.width}
                                height={icon.height}
                                cursor='pointer'
                                onClick={() => navigate(icon.route)}
                              />
                            </Tooltip>
                          </Card>
                        </Card>
                      </Card>
                    );
                  })}
                  <div style={{ 
                    borderTop: '1px solid #E0E0E0', 
                    width: '100%'
                  }} />
                </>
              )}

              {isClientEnabled && (isCommercial || isComite || isInstrumentator)&& CLIENT_ICON.map((icon) => {
                const isActive =
                  icon.matchRoutes?.some((route) => {
                    if (route instanceof RegExp) {
                      return route.test(location.pathname);
                    }
                    if (route === routes.clients) {
                      return location.pathname === route;
                    }
                    if (route === routes.clientDetail) {
                      return location.pathname.startsWith('/clientes/') && 
                             !location.pathname.startsWith('/clientes/list');
                    }
                    return location.pathname.startsWith(route);
                  }) ?? false;

                return (
                  <Card key={icon.name}>
                    <Card
                      display='flex'
                      variant={isActive ? (clientTheme ? 'primary' : 'success') : 'none'}
                      modifier='weak'
                      height='36px'
                      justifyContent='center'
                      width='36px'
                      alignItems='center'
                    >
                      <Card>
                        <Tooltip position='right' msg={icon.msg}>
                          <Icon
                            name={icon.name}
                            variant={isActive ? 'primary' : 'neutral'}
                            modifier='strong'
                            width={icon.width}
                            height={icon.height}
                            cursor='pointer'
                            onClick={() => navigate(icon.route)}
                          />
                        </Tooltip>
                      </Card>
                    </Card>
                  </Card>
                );
              })}

              {(isQuotationsEnabled || isGuaranteesEnabled) && (
                <Card key={SETTINGS_ICON.name}>
                  <Card
                    display='flex'
                    variant={SETTINGS_ICON.matchRoutes?.some((route) => {
                      if (typeof route === 'string') {
                        return location.pathname.startsWith(route);
                      }
                      return false;
                    }) ? (clientTheme ? 'primary' : 'success') : 'none'}
                    modifier='weak'
                    height='36px'
                    justifyContent='center'
                    width='36px'
                    alignItems='center'
                  >
                    <Card>
                      <Tooltip position='right' msg={SETTINGS_ICON.msg}>
                        <Icon
                          name={SETTINGS_ICON.name}
                          variant={SETTINGS_ICON.matchRoutes?.some((route) => {
                            if (typeof route === 'string') {
                              return location.pathname.startsWith(route);
                            }
                            return false;
                          }) ? 'primary' : 'neutral'}
                          modifier='strong'
                          width={SETTINGS_ICON.width}
                          height={SETTINGS_ICON.height}
                          cursor='pointer'
                          onClick={() => navigate(SETTINGS_ICON.route)}
                        />
                      </Tooltip>
                    </Card>
                  </Card>
                </Card>
              )}

              {isComite && (
                <Card key={USERS_ICON.name}>
                  <Card
                    display='flex'
                    variant={USERS_ICON.matchRoutes?.some((route) => {
                      if (typeof route === 'string') {
                        return location.pathname.startsWith(route);
                      }
                      return false;
                    }) ? (clientTheme ? 'primary' : 'success') : 'none'}
                    modifier='weak'
                    height='36px'
                    justifyContent='center'
                    width='36px'
                    alignItems='center'
                  >
                    <Card>
                      <Tooltip position='right' msg={USERS_ICON.msg}>
                        <Icon
                          name={USERS_ICON.name}
                          variant={USERS_ICON.matchRoutes?.some((route) => {
                            if (typeof route === 'string') {
                              return location.pathname.startsWith(route);
                            }
                            return false;
                          }) ? 'primary' : 'neutral'}
                          modifier='strong'
                          width={USERS_ICON.width}
                          height={USERS_ICON.height}
                          cursor='pointer'
                          onClick={() => navigate(USERS_ICON.route)}
                        />
                      </Tooltip>
                    </Card>
                  </Card>
                </Card>
              )}

            </Card>
          </Card>
          <div
            style={{
              width: '-webkit-fill-available',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <Card
              margin={{ top: '7', left: '2', right: '5', bottom: '7' }}
              justifyItems='center'
            >
              {children}
            </Card>
          </div>
        </Card>
      </Card>
    </div>
  );
};

export default Layout;